import PendoIconButton from '../icon-button/pendo-icon-button.vue.js';
import CalendarMonth from './calendar-month.vue.js';
import CalendarYear from './calendar-year.vue.js';
import CalendarDay from './calendar-day.vue.js';
import { pageIsValid, pageIsAfterPage, pageIsBeforePage, pageForDate } from './utils/helpers.js';

const modeMap = {
  day: 'month',
  month: 'year',
  year: 'decade'
};
var script = {
  name: 'CalendarPane',
  components: {
    CalendarDay,
    CalendarMonth,
    CalendarYear,
    PendoIconButton
  },
  inject: ['state'],
  props: {
    page: {
      type: Object,
      required: true
    },
    canMove: {
      type: Function,
      default: () => true
    },
    minPage: {
      type: Object,
      default: null
    },
    maxPage: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      mode: 'day',
      yearIndex: 0,
      yearGroupIndex: 0
    };
  },
  computed: {
    canMovePrev() {
      if (!this.minPage || !pageIsValid(this.minPage)) {
        return true;
      }
      return {
        day: pageIsAfterPage(this.page, this.minPage),
        month: this.yearIndex > this.minPage.year,
        year: this.firstYear - 1 > this.minPage.year
      }[this.mode];
    },
    canMoveNext() {
      if (!this.maxPage || !pageIsValid(this.maxPage)) {
        return true;
      }
      return {
        day: pageIsBeforePage(this.page, this.maxPage),
        month: this.yearIndex < this.maxPage.year,
        year: this.lastYear + 1 < this.maxPage.year
      }[this.mode];
    },
    masks() {
      return this.state.locale.masks;
    },
    count() {
      return this.state.visibleColumnCount;
    },
    isTitleInteractive() {
      return !(this.count > 1);
    },
    isHeaderRightButtonVisible() {
      return this.page.position === this.count - 1;
    },
    isHeaderLeftButtonVisible() {
      return this.page.position === 0;
    },
    weekdays() {
      return this.state.locale.getWeekdayDates().map(date => this.state.locale.format(date, this.masks.weekdays));
    },
    days() {
      return this.page.days;
    },
    month() {
      if (this.page) {
        return this.page.month || 0;
      }
      return 0;
    },
    year() {
      if (this.page) {
        return this.page.year || 0;
      }
      return 0;
    },
    title() {
      const title = {
        day: this.page.title,
        month: this.yearIndex,
        year: `${this.firstYear} - ${this.lastYear}`
      }[this.mode];
      return String(title);
    },
    months() {
      const {
        month: currentMonth,
        year: currentYear
      } = pageForDate(new Date());
      return this.state.locale.getMonthDates().map(date => this.state.locale.format(date, this.masks.months)).map((label, i) => {
        const month = i + 1;
        return {
          label,
          selected: month === this.month && this.yearIndex === this.year,
          current: month === currentMonth && this.yearIndex === currentYear,
          disabled: !this.canMove({
            month,
            year: this.yearIndex
          }),
          key: i + 1
        };
      });
    },
    years() {
      const {
        year: currentYear
      } = pageForDate(new Date());
      const startYear = this.yearGroupIndex * 12;
      const endYear = startYear + 12;
      const minYear = this.minPage ? this.minPage.year : null;
      const maxYear = this.maxPage ? this.maxPage.year : null;
      const items = [];
      for (let year = startYear; year < endYear; year += 1) {
        items.push({
          year,
          label: year,
          key: year,
          selected: year === this.year,
          current: year === currentYear,
          disabled: minYear && minYear > year || maxYear && maxYear < year
        });
      }
      return items;
    },
    firstYear() {
      return [].concat(this.years.map(i => i.year)).shift();
    },
    lastYear() {
      return [].concat(this.years.map(i => i.year)).pop();
    },
    prevAriaLabel() {
      return `previous ${modeMap[this.mode]}`;
    },
    nextAriaLabel() {
      return `next ${modeMap[this.mode]}`;
    }
  },
  watch: {
    year() {
      this.yearIndex = this.year;
    },
    yearIndex(val) {
      this.yearGroupIndex = Math.floor(val / 12);
    }
  },
  created() {
    this.yearIndex = this.year;
  },
  methods: {
    move(page) {
      this.$emit('update:page', page);
    },
    toggleMode() {
      if (this.count > 1) {
        return;
      }
      const nextMode = {
        day: 'month',
        month: 'year',
        year: 'month'
      }[this.mode];
      this.mode = nextMode;
    },
    monthClick(month) {
      this.move({
        month,
        year: this.yearIndex
      });
      this.mode = 'day';
    },
    yearClick(year) {
      this.yearIndex = year;
      this.mode = 'month';
    },
    movePrev() {
      if (this.mode === 'day') {
        return this.$emit('move-prev');
      }
      if (this.mode === 'month') {
        this.yearIndex--;
      }
      this.yearGroupIndex--;
    },
    moveNext() {
      if (this.mode === 'day') {
        return this.$emit('move-next');
      }
      if (this.mode === 'month') {
        this.yearIndex++;
      }
      this.yearGroupIndex++;
    }
  }
};

export { script as default };
//# sourceMappingURL=calendar-pane.vue2.js.map
